import { MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';

const CatalogueProductCard = ({ product, index = 0 }) => {
  const whatsappNumber = '+919619977421';
  
  const generateWhatsAppMessage = () => {
    if (product.isPlaceholder) return '';
    
    return encodeURIComponent(
      `Hi! I'm interested in:\n\n` +
      `Product: ${product.name}\n` +
      `Price: ${product.price}\n` +
      `Quantity: [Your Quantity]\n` +
      `City: [Your City]\n` +
      `Use case: [Your Use Case]`
    );
  };

  if (product.isPlaceholder) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        className="bg-brand-ivory rounded-sm border-2 border-dashed border-brand-pearl/20 p-8"
        data-testid={`placeholder-product-${index}`}
      >
        <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center">
          <div className="w-16 h-16 rounded-full bg-brand-timberwolf flex items-center justify-center mb-4">
            <span className="text-3xl text-brand-pearl">+</span>
          </div>
          <h3 className="text-lg font-heading font-semibold text-brand-pearl mb-2">
            {product.placeholder || 'Product Coming Soon'}
          </h3>
          <p className="text-sm text-brand-walnut">
            More products will be added here
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="bg-brand-ivory rounded-sm border border-brand-pearl/10 overflow-hidden card-hover"
      data-testid={`catalogue-product-${index}`}
      itemScope
      itemType="https://schema.org/Product"
    >
      {/* Product Image */}
      <div className="parallax-img">
        <img
          src={product.image}
          alt={`${product.name} - ${product.description.substring(0, 50)}...`}
          className="w-full h-64 object-cover"
          itemProp="image"
          loading="lazy"
        />
      </div>

      {/* Product Details */}
      <div className="p-6">
        {/* Name */}
        <h3 className="text-xl font-heading font-semibold text-brand-pearl mb-3" itemProp="name" data-testid="product-name">
          {product.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-brand-walnut leading-relaxed mb-4" itemProp="description" data-testid="product-description">
          {product.description}
        </p>

        {/* Specifications Grid */}
        <div className="space-y-2 mb-4 pb-4 border-b border-brand-pearl/10">
          {product.fabric && (
            <div className="flex justify-between text-sm">
              <span className="text-brand-walnut font-medium">Fabric:</span>
              <span className="text-brand-pearl" data-testid="product-fabric">{product.fabric}</span>
            </div>
          )}
          {product.fit && (
            <div className="flex justify-between text-sm">
              <span className="text-brand-walnut font-medium">Fit:</span>
              <span className="text-brand-pearl">{product.fit}</span>
            </div>
          )}
          {product.color && (
            <div className="flex justify-between text-sm">
              <span className="text-brand-walnut font-medium">Color:</span>
              <span className="text-brand-pearl">{product.color}</span>
            </div>
          )}
          {product.moq && (
            <div className="flex justify-between text-sm">
              <span className="text-brand-walnut font-medium">MOQ:</span>
              <span className="text-brand-pearl font-semibold" data-testid="product-moq">{product.moq}</span>
            </div>
          )}
          {product.deliveryTime && (
            <div className="flex justify-between text-sm">
              <span className="text-brand-walnut font-medium">Delivery:</span>
              <span className="text-brand-pearl" data-testid="product-delivery">{product.deliveryTime}</span>
            </div>
          )}
        </div>

        {/* Price */}
        {product.price && (
          <div className="mb-4" itemProp="offers" itemScope itemType="https://schema.org/Offer">
            <div className="text-2xl font-bold text-brand-pearl" data-testid="product-price">
              <span itemProp="price">{product.price}</span>
              <meta itemProp="priceCurrency" content="INR" />
            </div>
            <p className="text-xs text-brand-walnut mt-1">Shipping included</p>
          </div>
        )}

        {/* WhatsApp CTA */}
        <a
          href={`https://wa.me/${whatsappNumber}?text=${generateWhatsAppMessage()}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-enquiry-btn"
          className="w-full flex items-center justify-center px-6 py-3 bg-brand-green text-white font-medium rounded-sm hover:bg-brand-green/90 transition-colors"
        >
          <MessageSquare className="mr-2 w-4 h-4" />
          Send Enquiry on WhatsApp
        </a>
      </div>
    </motion.div>
  );
};

export default CatalogueProductCard;